'use client'

import type { ComponentProps, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { SidebarMenuSubButton } from '@/components/ui/sidebar-menu'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import {
    CheckCircleIcon,
    FileArrowUpIcon,
    FileIcon,
    GearSixIcon,
    GlobeIcon,
    TrashIcon,
    XIcon
} from '@/components/icons/icon-set'

/*
 * GoodUnited / WritingStyle. Figma: 10 / Components / Chat / Writing-style resources and composition.
 * The parts of the Supporter Response writing-style settings: a section card with a Content slot, the file
 * upload zone, file and website resource rows, and the settings navigation.
 */

/* WritingStyle/Section: a Card with 16px corners, a 24px icon, a Poppins / text-lg title, a description and a Content slot. */
interface WritingStyleSectionProps extends Omit<ComponentProps<'div'>, 'title'> {
    icon: ReactNode
    title: ReactNode
    description?: ReactNode
}

function WritingStyleSection({ icon, title, description, className, children, ...props }: WritingStyleSectionProps) {
    return (
        <Card data-slot='writing-style-section' className={cn('scroll-mt-6 gap-5 rounded-2xl', className)} {...props}>
            <CardHeader className='gap-2'>
                <CardTitle className='text-lg [&_svg]:size-6'>
                    {icon}
                    {title}
                </CardTitle>
                {description && (
                    <CardDescription>
                        {description}
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent>
                {children}
            </CardContent>
        </Card>
    )
}

/* WritingStyle/UploadZone: drop files or choose one, on Colors/surface/subtle with a dashed border. */
interface UploadZoneProps extends Omit<ComponentProps<'div'>, 'onDrop'> {
    label?: string
    accept?: string
    multiple?: boolean
    onFiles: (files: File[]) => void
}

function UploadZone({ label = 'Drag files here or choose a file', accept, multiple = true, onFiles, className, ...props }: UploadZoneProps) {
    const input = useRef<HTMLInputElement>(null)
    const [over, setOver] = useState(false)

    return (
        <div
            data-slot='upload-zone'
            data-over={over || undefined}
            onDragOver={event => {
                event.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={event => {
                event.preventDefault()
                setOver(false)
                const files = Array.from(event.dataTransfer.files)
                if (files.length) onFiles(multiple ? files : files.slice(0, 1))
            }}
            className={cn(
                'flex flex-wrap items-center justify-center gap-3 rounded-lg border border-dashed border-border-default bg-surface-subtle p-4 transition-colors',
                over && 'border-border-focus bg-surface-muted',
                className
            )}
            {...props}
        >
            <FileArrowUpIcon className='size-6 shrink-0 text-icon-secondary' aria-hidden='true' />
            <span className='text-sm text-text-secondary'>
                {label}
            </span>
            <Button variant='outline' size='sm' onClick={() => input.current?.click()}>
                Choose file
            </Button>
            <input
                ref={input}
                type='file'
                accept={accept}
                multiple={multiple}
                className='sr-only'
                tabIndex={-1}
                aria-hidden='true'
                onChange={event => {
                    const files = Array.from(event.target.files ?? [])
                    if (files.length) onFiles(files)
                    event.target.value = ''
                }}
            />
        </div>
    )
}

/* WritingStyle/ResourceRow: an uploaded file or a connected website with its ready check and an optional remove. */
interface ResourceRowProps extends Omit<ComponentProps<'div'>, 'children'> {
    type?: 'file' | 'website'
    label: string
    onRemove?: () => void
}

function ResourceRow({ type = 'file', label, onRemove, className, ...props }: ResourceRowProps) {
    return (
        <div
            data-slot='resource-row'
            data-type={type}
            className={cn('flex min-h-12 items-center gap-3 rounded-lg bg-surface-subtle p-3', className)}
            {...props}
        >
            <span className='inline-flex shrink-0 text-icon-secondary [&_svg]:size-5' aria-hidden='true'>
                {type === 'file' ? <FileIcon /> : <GlobeIcon />}
            </span>
            <span className='min-w-0 flex-1 truncate text-sm text-text-primary'>
                {label}
            </span>
            <CheckCircleIcon className='size-4 shrink-0 text-status-success-foreground' aria-label='Ready' role='img' />
            {onRemove && (
                <Button variant='outline' size='icon-sm' onClick={onRemove} aria-label={`Remove ${label}`}>
                    <TrashIcon aria-hidden='true' />
                </Button>
            )}
        </div>
    )
}

/*
 * WritingStyle/Navigation: Close, the settings heading and one sub button per section (220px).
 * On phones only Close and the heading show; the sections follow one another below.
 */
interface WritingStyleNavProps extends Omit<ComponentProps<'nav'>, 'onSelect'> {
    items: { id: string, label: string }[]
    active: string
    onSelect: (id: string) => void
    onClose?: () => void
}

function WritingStyleNav({ items, active, onSelect, onClose, className, ...props }: WritingStyleNavProps) {
    return (
        <nav data-slot='writing-style-nav' aria-label='Writing style sections' className={cn('flex flex-col gap-4 bg-surface-raised p-4', className)} {...props}>
            {onClose && (
                <Button variant='ghost' size='sm' className='self-start' onClick={onClose}>
                    <XIcon aria-hidden='true' />
                    Close
                </Button>
            )}
            <p className='flex items-center gap-2 text-sm font-medium text-text-primary'>
                <GearSixIcon className='size-5 shrink-0' aria-hidden='true' />
                Set up your writing style
            </p>
            <div className='hidden flex-col gap-4 md:flex'>
                {items.map(item => (
                    <SidebarMenuSubButton
                        key={item.id}
                        surface='surface'
                        label={item.label}
                        active={item.id === active}
                        onClick={() => onSelect(item.id)}
                        className={cn(item.id === active && 'bg-surface-muted font-normal text-text-secondary before:bg-border-subtle')}
                    />
                ))}
            </div>
        </nav>
    )
}

export { ResourceRow, UploadZone, WritingStyleNav, WritingStyleSection }
